import { HttpClient, HttpEvent, HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { CustomListDropdown } from 'src/app/interfaces/custom-list/custom-list-dropdown.interface';
import { ConstructionSiteImportModel } from 'src/app/models/imports/construction-site-import.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { environment } from 'src/environments/environment';
import { ConstructionSiteModel } from './../../models/archive/construction-site.model';
import { CostCenterModel } from './../../models/archive/cost-center.model';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';

@Injectable({
  providedIn: 'root'
})
export class ConstructionSiteService {

  constructor(private _httpClient: HttpClient) { }

  createConstructionSite(constructionSiteContractModel: ConstructionSiteModel): Observable<ConstructionSiteModel> {
    return this._httpClient.post<ConstructionSiteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.CreateConstructionSite}`,
      constructionSiteContractModel);
  }

  createCostCenter(constructionSiteContractModel: ConstructionSiteModel): Observable<CostCenterModel> {
    return this._httpClient.post<CostCenterModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.CreateCostCenter}`,
      constructionSiteContractModel);
  }

  deactivateConstructionSites(constructionSiteIds: number[]): Observable<LoadPreInvoicingModel> {
    return this._httpClient.put<LoadPreInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.DeactivateConstructionSitesAsync}`,
      constructionSiteIds);
  }

  deleteConstructionSite(constructionSiteId: number): Observable<ConstructionSiteModel> {
    return this._httpClient.delete<ConstructionSiteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.DeleteConstructionSite}/${constructionSiteId}`);
  }

  downloadGuideImport(): Observable<HttpEvent<Blob>> {
    return this._httpClient.request(new HttpRequest('GET', `${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.DownloadGuideImport}`,
      null, { reportProgress: true, responseType: 'blob' }));
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.ExportGeneralConsultation}`, filters, {
      params,
      responseType: 'blob',
    });
  }

  exportImportErrors(alerts: PreInvoicingAlertModel[]): Observable<Blob> {
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.ExportImportErrors}`, alerts,
      {
        responseType: 'blob'
      }
    );
  }

  getAllClientsForSelect(): Observable<Array<CustomListDropdown>> {
    return this._httpClient.get<Array<CustomListDropdown>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.GetAllClientsForSelect}`,);
  }

  getAllConceptsForSelect(): Observable<Array<CustomListDropdown>> {
    return this._httpClient.get<Array<CustomListDropdown>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.GetAllConceptsForSelect}`);
  }

  getAllConstructionSitesByClientIdForSelectAsync(clientId: number): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.GetAllConstructionSitesByClientIdForSelectAsync}/${clientId}`
    );
  }

  getAllConstructionSitesByKeysForSelectAsync(branchId: number, clientId: number): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.GetAllConstructionSitesByKeysForSelectAsync}/${branchId}/${clientId}`
    );
  }

  getAllConstructionSitesWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.GetAllConstructionSitesWithPagination}`,
      filters, { params: params }
    );
  }

  getAllConstructionSitesForSelect(branchId: number): Observable<Array<CustomListDropdown>> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<Array<CustomListDropdown>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.GetAllConstructionSitesForSelectAsync}`,
      { params: params });
  }

  getConstructionSitesForSelection(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.GetConstructionSitesForSelectionAsync}`,
      filters, { params: params }
    );
  }

  getAllSellersForSelect(branchId: number): Observable<Array<CustomListDropdown>> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<Array<CustomListDropdown>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.GetAllSellersForSelect}`, { params: params });
  }

  getAllZonesForSelect(branchId: number): Observable<Array<CustomListDropdown>> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<Array<CustomListDropdown>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.GetAllZonesForSelect}`, { params: params });
  }

  getClientByIdForRegisterAsync(clientId: number): Observable<any> {
    const params = new HttpParams().set('clientId', clientId);
    return this._httpClient.get<any>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.GetClientByIdForRegisterAsync}`, { params: params });
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.GetColumnsForGeneralConsultation}`);
  }

  getColumnsForSelection(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.GetColumnsForSelectionAsync}`);
  }

  getConstructionSiteByBranchId(branchId: number): Observable<Array<ConstructionSiteModel>> {
    return this._httpClient.get<Array<ConstructionSiteModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.GetConstructionSiteByBranchId}/${branchId}`);
  }

  getConstructionSiteByClientId(clientId: number): Observable<Array<ConstructionSiteModel>> {
    return this._httpClient.get<Array<ConstructionSiteModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.GetConstructionSiteByClientId}/${clientId}`);
  }

  getConstructionSiteByCode(branchId: number, code: number): Observable<ConstructionSiteModel> {
    return this._httpClient.get<ConstructionSiteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.GetConstructionSiteByCode}/${branchId}/${code}`);
  }

  getConstructionSiteById(constructionSiteId: number): Observable<ConstructionSiteModel> {
    return this._httpClient.get<ConstructionSiteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.GetConstructionSiteById}/${constructionSiteId}`);
  }

  getFirstConstructionSiteByKeysAsync(branchId: number): Observable<ConstructionSiteModel> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<ConstructionSiteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.GetFirstConstructionSiteByKeys}`, { params: params });
  }

  getLastConstructionSiteByKeysAsync(branchId: number): Observable<ConstructionSiteModel> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<ConstructionSiteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.GetLastConstructionSiteByKeys}`, { params: params });
  }

  getNextConstructionSiteByKeysAsync(branchId: number, code: number): Observable<ConstructionSiteModel> {
    const params = new HttpParams().set('branchId', branchId).set('code', code);
    return this._httpClient.get<ConstructionSiteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.GetNextConstructionSiteByKeys}`, { params: params });
  }

  getPreviusConstructionSiteByKeysAsync(branchId: number, code: number): Observable<ConstructionSiteModel> {
    const params = new HttpParams().set('branchId', branchId).set('code', code);
    return this._httpClient.get<ConstructionSiteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.GetPreviusConstructionSiteByKeys}`, { params: params });
  }

  getSectorsByCityForSelect(cityId: number): Observable<Array<CustomListDropdown>> {
    const params = new HttpParams().set('cityId', cityId);
    return this._httpClient.get<Array<CustomListDropdown>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.GetSectorsByCityForSelect}`, { params: params });
  }

  importConstructionSites(constructionSiteImports: Array<ConstructionSiteImportModel>): Observable<LoadPreInvoicingModel> {
    return this._httpClient.post<LoadPreInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.ImportConstructionSites}`,
      constructionSiteImports);
  }

  updateConstructionSite(constructionSiteContractModel: ConstructionSiteModel): Observable<ConstructionSiteModel> {
    return this._httpClient.put<ConstructionSiteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.constructionSite.UpdateConstructionSite}`,
      constructionSiteContractModel);
  }
}
